import { useEditor } from '../store/useEditor'
import { isStrand } from '../types/document'
import type { SelectionBox } from '../render/StageView'
import type { StrandMetrics } from '../render/scene'

/**
 * The contextual controls for a selected run.
 *
 * It follows the run rather than living in a permanent inspector, so the house
 * keeps the screen. It carries the one number that matters and the four things
 * a contractor actually does to a finished run. Everything else stays in the
 * panel on the right, where it does not cover the photo.
 */
export default function SelectionBadge({
  box,
  metrics,
}: {
  box: SelectionBox | null
  metrics: StrandMetrics[]
}) {
  const selectedId = useEditor((s) => s.selectedId)
  const layers = useEditor((s) => s.doc.layers)
  const present = useEditor((s) => s.present)
  const selectLayer = useEditor((s) => s.selectLayer)
  const updateStrand = useEditor((s) => s.updateStrand)
  const duplicateLayer = useEditor((s) => s.duplicateLayer)
  const deleteLayer = useEditor((s) => s.deleteLayer)
  const undo = useEditor((s) => s.undo)
  const canUndo = useEditor((s) => s.past.length > 0)

  if (present || !box || !selectedId) return null
  const layer = layers.find((l) => l.id === selectedId)
  if (!layer || !isStrand(layer)) return null

  const m = metrics.find((x) => x.id === selectedId)

  // Sit above the run, or below it when the run is near the top of the frame.
  const above = box.top > 96
  const style: React.CSSProperties = {
    left: box.midX,
    top: above ? box.top - 14 : box.bottom + 14,
    transform: above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
  }

  return (
    <div className="sel-badge" style={style}>
      {/*
        ICONS, AND ONE NUMBER THAT MEANS SOMETHING.

        This was five words in a row - Hide, Duplicate, Delete, Done - plus a
        bulb count, which is a lot of reading for a strip that sits on top of
        the customer's house. Icons are what every design tool uses here and
        they take a quarter of the space.

        The number is FEET. It was a bulb count, and a bulb count is not a
        thing anyone buys, sells or quotes: contractors work in linear feet and
        derive bulbs from spacing. "179 bulbs" also quietly depended on a
        spacing assumption nobody had made, so it looked precise and was not.
        With no scale on the photo it now says so, which is more use than a
        confident wrong number.

        Done is gone: clicking anywhere off the run already deselects it, so
        the button only existed to say something the canvas already does.
      */}
      {/*
        No name. "Roofline 3" is a label the software needs and the contractor
        does not - he can see which run is selected, it is the one with the
        handles on it - and it made this strip twice as wide for nothing.
        Actions only.
      */}

      {/*
        Feet when we know them, and nothing at all when we do not.
        "no scale yet" was saying the same thing the panel already says in a
        block titled Set the scale, and repeating it on top of the photo is
        nagging rather than informing.
      */}
      {/* The footage lived here. Hidden while this is a visual design tool. */}

      <button
        className="sb-icon"
        onClick={undo}
        disabled={!canUndo}
        title="Undo the last change"
        aria-label="Undo"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M9 14L4 9l5-5" />
          <path d="M4 9h10a6 6 0 010 12h-3" />
        </svg>
      </button>

      <button
        className="sb-icon"
        onClick={() => updateStrand(layer.id, { visible: !layer.visible })}
        title={layer.visible ? 'Hide this run' : 'Show this run'}
        aria-label={layer.visible ? 'Hide' : 'Show'}
      >
        {layer.visible ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M3 3l18 18M10.6 10.7a3 3 0 004.2 4.2" />
            <path d="M9.9 5.2A9.6 9.6 0 0112 5c6 0 10 7 10 7a17 17 0 01-3.2 3.9M6.2 6.7A17 17 0 002 12s4 7 10 7a9.7 9.7 0 004.1-.9" />
          </svg>
        )}
      </button>

      <button
        className="sb-icon"
        onClick={() => duplicateLayer(layer.id)}
        title="Make another just like it"
        aria-label="Duplicate"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <rect x="9" y="9" width="12" height="12" rx="2" />
          <path d="M5 15V5a2 2 0 012-2h10" />
        </svg>
      </button>

      <button
        className="sb-icon danger"
        onClick={() => {
          deleteLayer(layer.id)
          selectLayer(null)
        }}
        title="Delete this run"
        aria-label="Delete"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M6 7l1 13a1 1 0 001 1h8a1 1 0 001-1l1-13" />
        </svg>
      </button>
    </div>
  )
}
